import React, { useState, useEffect, useMemo } from 'react';
import { TableContainer, Grid } from '@mui/material';
import moment from 'moment';
import QRCode from 'react-qr-code'; 
import { useApi } from '../ContextApi/ContextApi';
import { readFontStyle, DEFAULT_ELEC_SIGNATURE_MSG } from './fontStyle';
import '../UI/Template.css';

const Template4 = ({ reportData = [], QRurl }) => {
  const [fontStyle, setFontStyle] = useState({});
  const [drs, setDrs] = useState([]);

  const currentDateTime = useMemo(() => new Date(), []);
  const username = `${sessionStorage.getItem("FirstName") || ""} ${sessionStorage.getItem("LastName") || ""}`.trim();
  const { Comment, isheader } = useApi();

  // Helper to safely parse and split range values
  const parseRange = (rangeStr) => {
    if (!rangeStr || typeof rangeStr !== 'string') return null;
    const parts = rangeStr.split('-').map((val) => Number(val.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return { min: parts[0], max: parts[1] };
    }
    return null;
  };

  const Resultcalculation = (range, result) => {
    const parsed = parseRange(range);
    if (!parsed || result === undefined || result === null || result === '') return '#1e293b';

    const numResult = Number(result);
    if (isNaN(numResult)) return '#1e293b';

    if (numResult < parsed.min) return '#2563eb'; // Blue for Low
    if (numResult > parsed.max) return '#dc2626'; // Red for High
    return '#1e293b'; // Normal
  };

  const Getlowhighvalue = (range, result) => {
    const parsed = parseRange(range);
    if (!parsed || result === undefined || result === null || result === '') return '';

    const numResult = Number(result);
    if (isNaN(numResult)) return '';

    if (numResult < parsed.min) return ' (Low)';
    if (numResult > parsed.max) return ' (High)';
    return '';
  };

  // Group report data efficiently
  const groupedData = useMemo(() => {
    return reportData.reduce((result, item) => {
      const category = item.categorY_CODE || 'GENERAL';
      const subCategory = item.suB_CATEGORY_CODE || 'DEFAULT';

      if (!result[category]) {
        result[category] = {
          categoryValue: item.categorY_VALUE || 'General Tests',
          subCategories: {},
        };
      }

      if (!result[category].subCategories[subCategory]) {
        result[category].subCategories[subCategory] = {
          subCategoryValue: item.suB_CATEGORY_VALUE || '',
          items: [],
        };
      }

      result[category].subCategories[subCategory].items.push(item);
      return result;
    }, {});
  }, [reportData]);

  const formatTextWithLineBreaks = (text) => {
    if (!text) return '-';
    return String(text).split('\n').map((line, index, arr) => (
      <React.Fragment key={index}>
        {line}
        {index < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  useEffect(() => {
    setFontStyle(readFontStyle());
    try {
      const doctors = JSON.parse(localStorage.getItem('DoctorsList'));
      if (doctors) setDrs(doctors);
    } catch (e) {
      console.error("Failed to parse dynamic styles/configuration from localStorage", e);
    }
  }, []);

  const image = localStorage.getItem('LogoImage');
  const signatureImage = localStorage.getItem('SignatureImage');
  const labName = useMemo(() => {
    return reportData.find((x) => x.laboratoryName)?.laboratoryName || "Diagnostic Laboratory";
  }, [reportData]);

  const patient = reportData?.[0] || {};
  const fontFamily = fontStyle?.fontfamily || 'Inter, -apple-system, sans-serif';
  const baseFontSize = fontStyle?.fontsize || '12px';
  const baseFontWeight = fontStyle?.fontweight || 'normal';
  const elecSignatureMsg = fontStyle?.electronicSignatureMsg || DEFAULT_ELEC_SIGNATURE_MSG;

  return (
    <div
      className="print-report"
      style={{
        fontFamily,
        fontSize: '12px',
        fontWeight: baseFontWeight,
        padding: '20px',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact'
      }}
    >
      <div 
        className="page" 
        style={{ 
          background: '#ffffff', 
          borderRadius: '12px', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)', 
          padding: '24px', 
          maxWidth: '900px', 
          margin: '0 auto' 
        }}
      >
        {/* Header Section */}
        <header className="print-header" style={{ marginBottom: '24px' }}>
          {isheader && image && (
            <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: '2px solid #e2e8f0', paddingBottom: '16px' }}>
              <img
                className="logo-img"
                src={`data:image/png;base64,${image}`}
                alt="Lab Logo"
                style={{ maxHeight: '100px', objectFit: 'contain' }}
              />
            </div>
          )}

          {/* Patient Details Card */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: '16px', background: '#f1f5f9', padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>{patient.patientName || 'N/A'}</span>
              <span style={{ color: '#64748b', fontSize: '11px' }}>
                <strong>MR No:</strong> <span style={{ color: '#0f172a' }}>{patient.patientID || 'N/A'}-{sessionStorage.getItem('LabID') || ''}</span>
              </span>
              <span style={{ color: '#64748b', fontSize: '11px' }}>
                <strong>{patient.sex?.toLowerCase() === 'male' ? 'Father Name:' : 'Father / Husband:'}</strong> <span style={{ color: '#0f172a' }}>{patient.guardianName || '-'}</span>
              </span>
              <span style={{ color: '#64748b', fontSize: '11px' }}>
                <strong>Age / Gender:</strong> <span style={{ color: '#0f172a' }}>{patient.age ? `${patient.age} Y` : '-'} / {patient.sex || '-'}</span>
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {QRurl && <QRCode value={QRurl} size={70} style={{ borderRadius: '6px' }} />}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'right' }}>
              <span style={{ color: '#64748b', fontSize: '11px' }}>
                <strong>Collected At:</strong> <span style={{ color: '#0f172a' }}>{patient.address || 'Main Branch'}</span>
              </span>
              <span style={{ color: '#64748b', fontSize: '11px' }}>
                <strong>Phone:</strong> <span style={{ color: '#0f172a' }}>{patient.contactNumber || '-'}</span>
              </span>
              <span style={{ color: '#64748b', fontSize: '11px' }}>
                <strong>Referred By:</strong> <span style={{ color: '#0f172a' }}>{patient.referredBy || 'Self'}</span>
              </span>
              <span style={{ color: '#64748b', fontSize: '11px' }}>
                <strong>Registered On:</strong> <span style={{ color: '#0f172a' }}>{patient.createdAt ? moment(patient.createdAt).format('D MMM YYYY') : '-'}</span>
              </span>
            </div>
          </div>
        </header>

        {/* Results Section */}
        <main className="report-body">
          {Object.keys(groupedData).map((category, index) => (
            <div className="category-section" key={index} style={{ marginBottom: '24px' }}>
              
              {/* Category Header */}
              <div style={{ background: '#0284c7', color: '#ffffff', padding: '8px 16px', borderRadius: '6px', fontWeight: '700', fontSize: '18px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {groupedData[category].categoryValue}
              </div>

              {Object.keys(groupedData[category].subCategories).map((subCategory, subIndex) => (
                <div key={subIndex} style={{ marginTop: '12px' }}>
                  {groupedData[category].subCategories[subCategory].subCategoryValue && (
                    <div style={{ fontWeight: '600', color: '#334155', padding: '4px 8px', fontSize: '12px', borderLeft: '3px solid #0284c7', marginBottom: '8px' }}>
                      {groupedData[category].subCategories[subCategory].subCategoryValue}
                    </div>
                  )}

                  <TableContainer style={{ boxShadow: 'none', background: 'transparent' }}>
                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', marginTop: '4px', fontSize: baseFontSize }}>
                      <thead>
                        <tr style={{ background: '#f8fafc' }}>
                          <th style={{ textAlign: 'left', padding: '10px', color: '#475569', fontWeight: '700', width: '40%', borderBottom: '2px solid #e2e8f0' }}>Investigation</th>
                          <th style={{ textAlign: 'left', padding: '10px', color: '#475569', fontWeight: '700', width: '20%', borderBottom: '2px solid #e2e8f0' }}>Result</th>
                          <th style={{ textAlign: 'left', padding: '10px', color: '#475569', fontWeight: '700', width: '20%', borderBottom: '2px solid #e2e8f0' }}>Unit</th>
                          <th style={{ textAlign: 'left', padding: '10px', color: '#475569', fontWeight: '700', width: '20%', borderBottom: '2px solid #e2e8f0' }}>Reference Range</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupedData[category].subCategories[subCategory].items.map((data, itemIdx) => {
                          const resultColor = Resultcalculation(data.normalRange, data.testResult);
                          const flag = Getlowhighvalue(data.normalRange, data.testResult);
                          const isOdd = itemIdx % 2 === 0;

                          return (
                            <tr key={itemIdx} style={{ background: isOdd ? '#ffffff' : '#f8fafc' }}>
                              <td style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#1e293b', fontWeight: '500' }}>
                                {data.testName}
                              </td>
                              <td style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', fontWeight: '700', color: resultColor }}>
                                {data.testResult}
                                {flag && (
                                  <span style={{ fontSize: baseFontSize, padding: '2px 6px', borderRadius: '4px', marginLeft: '6px', background: flag.includes('High') ? '#fee2e2' : '#dbeafe', color: resultColor }}>
                                    {flag.trim()}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>
                                {formatTextWithLineBreaks(data.unit)}
                              </td>
                              <td style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#64748b' }}>
                                {formatTextWithLineBreaks(data.normalRange)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </TableContainer>
                </div>
              ))}
            </div>
          ))}
        </main>

        {/* Footer Section */}
        <footer className="print-footer" style={{ marginTop: '32px', paddingTop: '16px', borderTop: '2px solid #e2e8f0' }}>
          {Comment && (
            <div style={{ marginBottom: '20px' }}>
              <span style={{ fontWeight: '700', color: '#334155', fontSize: '11px', textTransform: 'uppercase' }}>Report Remarks / Comments</span>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px 14px', marginTop: '4px', color: '#475569', fontSize: '11px' }}>
                <p style={{ margin: 0 }}>{Comment}</p>
              </div>
            </div>
          )}

          {/* Doctor Signatures */}
          {drs.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(drs.length, 3)}, 1fr)`, gap: '16px', marginBottom: '20px', textAlign: 'center' }}>
              {drs.map((doc, idx) => (
                <div key={idx} style={{ padding: '8px', borderTop: '1px dashed #cbd5e1' }}>
                  <div style={{ fontWeight: '700', color: '#0f172a' }}>{doc.drname}</div>
                  <div style={{ color: '#0284c7', fontSize: '10px', fontWeight: '500' }}>{doc.designation}</div>
                  <div style={{ color: '#64748b', fontSize: '10px' }}>{doc.description}</div>
                </div>
              ))}
            </div>
          )}

          {/* Electronic Signature */}
          <div className="t4-signature-section" style={{ marginBottom: '20px' }}>
            <Grid container justifyContent="flex-end" textAlign="center">
              <Grid item xs={4}>
                {signatureImage && (
                  <img
                    className="t4-signature-img"
                    src={`data:image/png;base64,${signatureImage}`}
                    alt="Electronic Signature"
                    style={{ maxHeight: '60px', marginBottom: '5px' }}
                  />
                )}
                <div style={{ fontWeight: '700', fontSize: '12px', color: '#0f172a' }}>
                  Electronically Signed By:
                </div>
                <div style={{ fontSize: '10px', color: '#64748b' }}>
                  {elecSignatureMsg}
                </div>
              </Grid>
            </Grid>
          </div>

          {/* Print Metadata & Copyright */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#94a3b8', fontSize: '10px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
            <div>
              <span><strong>Printed At:</strong> {moment(currentDateTime).format('DD-MMM-YYYY hh:mm A')}</span> &bull; <span><strong>Printed By:</strong> {username || 'System'}</span>
            </div>
            <div>
              &copy; {new Date().getFullYear()} <strong>{labName}</strong>. All rights reserved.
            </div>
          </div>
        </footer>

      </div>
    </div>
  );
};

export default Template4;